import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import MentorApplications from "@/components/mentor/MentorApplications";
import UpcomingAppointments from "@/components/mentor/UpcomingAppointments";
import MyMenteesSection from "@/components/mentor/MyMenteesSection";
import MentorLeaveReview from "@/components/mentor/MentorLeaveReview";
import ActiveSubscriptionsSection from "@/components/ActiveSubscriptionsSection";

import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, MessageSquare, Users, BookOpen } from "lucide-react";

const MentorDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, refreshUser } = useAuth();
  const [userName, setUserName] = useState("User");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [userId, setUserId] = useState<string>("");
  const [hasPendingReviews, setHasPendingReviews] = useState(false);

  useEffect(() => {
    document.title = "Mentor Dashboard | EMU Mentoring";
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        console.log("Fetching user data for mentor dashboard");
        
        // Prioritize auth context over localStorage
        if (user) {
          console.log("Using auth context user data:", user);
          setUserName(user.firstName || "User");
          setUserId(user.id);
          
          // Check profile type from auth context first
          if (user.profileType !== "mentor" && user.profileType !== "both") {
            navigate("/dashboard");
            return;
          }

          // Fetch fresh avatar from database
          const { data, error } = await supabase
            .from('profiles')
            .select('avatar_url')
            .eq('id', user.id)
            .single();

          if (!error && data?.avatar_url) {
            setAvatarUrl(data.avatar_url);
          }
        } else {
          // Fallback to localStorage if auth context is not ready
          const userData = localStorage.getItem("userData");
          if (userData) {
            const parsed = JSON.parse(userData);
            if (parsed.firstName) {
              setUserName(parsed.firstName);
            }
            
            if (parsed.profileType !== "mentor" && parsed.profileType !== "both") {
              navigate("/dashboard");
              return;
            }

            if (parsed.id) {
              setUserId(parsed.id);
              
              const { data, error } = await supabase
                .from('profiles')
                .select('avatar_url')
                .eq('id', parsed.id)
                .single();

              if (!error && data?.avatar_url) {
                setAvatarUrl(data.avatar_url);
              }
            }
          } else {
            navigate("/login");
          }
        }
      } catch (e) {
        console.error("Error in fetchUserData:", e);
        // If there's an error and we have refreshUser, try refreshing
        if (refreshUser) {
          try {
            await refreshUser();
          } catch (refreshError) {
            console.error("Error refreshing user:", refreshError);
          }
        }
      }
    };

    fetchUserData();
  }, [navigate, user, refreshUser]);

  const handleNavigation = (route: string) => {
    navigate(route);
  };

  const handleReviewSubmitted = () => {
    console.log('DEBUG: Review submitted in mentor dashboard, triggering refresh');
    // The MentorApplications component will listen for the custom event
    // dispatched by MentorLeaveReview, so no additional action needed here
  };

  const ActionCard = ({ 
    icon, 
    title, 
    description, 
    route 
  }: { 
    icon: React.ReactNode, 
    title: string, 
    description: string,
    route: string
  }) => (
    <Card 
      className="bg-white rounded-lg shadow-sm border p-3 flex flex-col items-center hover:shadow-lg hover:bg-gray-50/50 transition-all duration-300 cursor-pointer min-h-[110px] sm:min-h-[120px] justify-center group" 
      onClick={() => handleNavigation(route)}
    >
      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-100 group-hover:bg-blue-200 transition-colors duration-300 flex items-center justify-center mb-3">
        {icon}
      </div>
      <h3 className="font-semibold text-gray-800 mb-1 text-sm sm:text-base text-center leading-tight">{title}</h3>
      <p className="text-xs sm:text-sm text-gray-500 text-center mb-3 hidden sm:block leading-tight">{description}</p>
      <Button variant="ghost" size="sm" className="text-emu-blue hover:text-emu-blue/80 p-1 group-hover:bg-blue-50 transition-colors duration-200">
        <ArrowRight size={16} className="sm:w-4 sm:h-4" />
      </Button>
    </Card>
  );

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <main className="flex-grow pt-0 pb-12">
        {/* Hero Header Section */}
        <div className="w-full bg-cover bg-center h-48 xs:h-56 sm:h-64 relative" style={{ backgroundImage: "url('/lovable-uploads/63560c3c-d7c3-4d91-b852-d2cd79229bf8.png')" }}>
          <div className="absolute inset-0 bg-emu-blue/20"></div>
          <div className="container mx-auto px-4 sm:px-6 h-full flex items-end pb-6 xs:pb-8 relative z-10">
            <div>
              <div className="uppercase text-emu-blue font-medium tracking-wide mb-1 text-xs xs:text-sm">
                MENTOR'S
              </div>
              <h1 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900">
                Dashboard
              </h1>
            </div>
          </div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6">
          {/* Welcome Card and Profile Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mt-6 sm:mt-8">
            <Card className="lg:col-span-2 bg-white rounded-lg shadow-sm border p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-center sm:items-start">
                <div className="w-20 h-20 sm:w-24 sm:h-24 relative">
                  <Avatar className="w-20 h-20 sm:w-24 sm:h-24 border-4 border-white shadow-md">
                    <AvatarImage src={avatarUrl} alt={userName} />
                    <AvatarFallback className="text-2xl sm:text-4xl font-medium">
                      {userName.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">
                    Welcome, {userName}
                  </h2>
                  <p className="text-gray-500 mb-2 text-sm sm:text-base">
                    to your Mentor Dashboard
                  </p>
                </div>
              </div>
            </Card>
            
            <Card className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold text-gray-800 text-xs sm:text-sm">Account Settings</h3>
                <ArrowRight size={16} className="text-gray-400 flex-shrink-0" />
              </div>
              <div className="text-xs sm:text-sm text-gray-500 mb-4">Manage your password and payment information</div>
              <Button 
                className="w-full bg-emu-blue hover:bg-emu-blue/90 text-sm sm:text-base h-10 sm:h-11"
                onClick={() => handleNavigation(`/account`)}
              >
                <span className="hidden sm:inline">Account Settings</span>
                <span className="sm:hidden">Account Settings</span>
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Card>
          </div>

          <div className="max-w-7xl mx-auto mt-6 sm:mt-8">
            {/* Quick Actions - Card Style Layout */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5 mb-8 sm:mb-10">
              <ActionCard 
                icon={<Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-emu-blue" />}
                title="Calendar"
                description="Manage your availability"
                route="/calendar"
              />
              
              <ActionCard 
                icon={<MessageSquare className="h-4 w-4 sm:h-5 sm:w-5 text-emu-blue" />}
                title="Messages"
                description="Chat with mentees"
                route="/messages"
              />
              
              <ActionCard 
                icon={<Users className="h-4 w-4 sm:h-5 sm:w-5 text-emu-blue" />}
                title="Profile"
                description="Update your profile"
                route={`/mentor-profile/${userId}`}
              />
              
              <ActionCard 
                icon={<BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-emu-blue" />}
                title="Academy"
                description="Manage courses"
                route="/my-academy"
              />
            </div>
            
            {/* Active Subscriptions Section */}
            <div className="mt-12">
              <ActiveSubscriptionsSection 
                userId={userId} 
                userType="mentor" 
              />
            </div>
            
            {/* Dashboard Sections */}
            <div className="mt-12">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">My Appointments</h2>
              <UpcomingAppointments />
            </div>
            
            <div className="mt-12">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Leave Review</h2>
              <MentorLeaveReview 
                onReviewSubmitted={handleReviewSubmitted} 
                onPendingReviewsChange={setHasPendingReviews}
              />
            </div>
            
            <div className="mt-12">
              <MentorApplications />
            </div>
            
            
            <div className="mt-12">
              <MyMenteesSection />
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MentorDashboard;
